import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ApiError } from '../../services/api/errors.js';

/** The form-level error slot `<Form>` renders above the fields. */
export const ROOT_SERVER_ERROR = 'root.server' as const;

interface IssueDetail {
  path: string;
  message: string;
}

function isIssueDetail(value: unknown): value is IssueDetail {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.path === 'string' && typeof record.message === 'string';
}

/** True when `path` (dot notation) names a value the form holds, i.e. a field the person can see. */
function isFormPath(values: unknown, path: string): boolean {
  if (!path) return false;
  let node: unknown = values;
  for (const segment of path.split('.')) {
    if (node === null || typeof node !== 'object' || !(segment in (node as Record<string, unknown>))) return false;
    node = (node as Record<string, unknown>)[segment];
  }
  return true;
}

/**
 * Puts a rejected submit's errors where the person will see them.
 *
 * - A validation body's `details: [{ path, message }]` becomes per-field errors.
 * - A `ServiceError` naming a `field` becomes that field's error.
 * - Anything that names no visible field (or is not an `ApiError` at all) becomes the form-level
 *   error, shown by `<Form>` above the fields — never swallowed.
 *
 * A path counts as visible when the form's values contain it, so every form passes complete
 * `defaultValues` (`useZodForm` requires them).
 */
export function applyServerErrors<TValues extends FieldValues, TContext, TTransformed>(
  form: UseFormReturn<TValues, TContext, TTransformed>,
  error: unknown,
): void {
  const values = form.getValues();
  const unplaced: string[] = [];

  if (error instanceof ApiError) {
    const details = Array.isArray(error.details) ? error.details.filter(isIssueDetail) : [];
    for (const detail of details) {
      if (isFormPath(values, detail.path)) {
        form.setError(detail.path as Path<TValues>, { type: 'server', message: detail.message });
      } else {
        unplaced.push(detail.path ? `${detail.path}: ${detail.message}` : detail.message);
      }
    }

    if (error.field && isFormPath(values, error.field)) {
      form.setError(error.field as Path<TValues>, { type: 'server', message: error.message });
    } else if (details.length === 0 || unplaced.length > 0) {
      unplaced.unshift(error.message);
    }
  } else {
    unplaced.push(error instanceof Error ? error.message : 'The request failed. Try again.');
  }

  if (unplaced.length > 0) {
    form.setError(ROOT_SERVER_ERROR, { type: 'server', message: unplaced.join(' · ') });
  }
}
